import * as API from '../constants/API';
import * as AWSconst from '../constants/AWS';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { AwsCredentialIdentity } from '@aws-sdk/types';
import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { asyncGetSession } from '../actions/Auth/api';
import { CognitoUserSession } from 'amazon-cognito-identity-js';

export class DayCareApi {
  api: AxiosInstance;

  static async ClientFactory() {
    const cognitoUser = AWSconst.userPool().getCurrentUser();
    if (cognitoUser === null) {
      throw new Error('the user is not logged in.');
    }
    const session = await asyncGetSession(cognitoUser);
    return new DayCareApi(session);
  }

  constructor(session: CognitoUserSession) {
    this.api = axios.create({
      baseURL: API.daycareApiRoot,
      headers: {
        Authorization: session.getIdToken().getJwtToken(),
      },
    });
  }
}

export class DayCareApiIamAuth {
  api: AxiosInstance;

  static async ClientFactory() {
    const cognitoUser = AWSconst.userPool().getCurrentUser();
    if (cognitoUser === null) {
      throw new Error('the user is not logged in.');
    }
    const session = await asyncGetSession(cognitoUser);

    const region = process.env.REACT_APP_AWS_REGION || '';
    const identityPoolId = process.env.REACT_APP_AWS_IDENTITY_POOL_ID || '';

    // AWS SDK v3: Get credentials from Cognito Identity Pool
    const credentialsProvider = fromCognitoIdentityPool({
      identityPoolId,
      logins: {
        [AWSconst.cognitoIdpURL]: session.getIdToken().getJwtToken(),
      },
      clientConfig: { region },
    });

    const credentials = await credentialsProvider();

    return new DayCareApiIamAuth(credentials, region);
  }

  constructor(credentials: AwsCredentialIdentity, region: string) {
    this.api = axios.create({
      baseURL: API.daycareApiRoot,
    });

    // Add AWS SigV4 signing interceptor
    this.api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      // Build full URL the same way Axios does (concatenation, not URL resolution)
      const fullUrl = (config.baseURL || '') + (config.url || '');
      const url = new URL(fullUrl);

      // Add query params from config.params (Axios adds these later, but we need them for signing)
      if (config.params) {
        Object.keys(config.params).forEach(key => {
          const value = config.params[key];
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        });
      }

      const method = config.method?.toUpperCase() || 'GET';

      // For GET/DELETE requests, body should be undefined
      // For POST/PUT, serialize body to JSON string (same as Axios does)
      let body: string | undefined;
      if (method !== 'GET' && method !== 'DELETE' && config.data !== undefined) {
        body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
      }

      // Build headers for signing
      // IMPORTANT: 'host' header MUST be included in the signature
      // For POST/PUT, 'content-type' and 'content-length' must also be included
      const headersRecord: Record<string, string> = {
        host: url.hostname,
      };

      // For POST/PUT requests, include content-type and content-length in signature
      if (method === 'POST' || method === 'PUT') {
        const contentType = config.headers?.get('content-type');
        if (contentType && typeof contentType === 'string') {
          headersRecord['content-type'] = contentType;
        }
        // Calculate content-length from body
        if (body !== undefined) {
          headersRecord['content-length'] = String(new TextEncoder().encode(body).length);
        }
      }

      if (config.headers) {
        Object.keys(config.headers).forEach(key => {
          const value = config.headers.get(key);
          if (value && typeof value === 'string') {
            const lowerKey = key.toLowerCase();
            // Only include AWS-specific headers in signature
            if (lowerKey.startsWith('x-amz-')) {
              headersRecord[lowerKey] = value;
            }
          }
        });
      }

      // Build query parameters object for signing
      const queryParams: Record<string, string> = {};
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      // Create HttpRequest for signing
      // Use 'query' property instead of appending to path for proper signature calculation
      const httpRequestOptions: any = {
        method,
        protocol: url.protocol,
        hostname: url.hostname,
        port: url.port ? Number(url.port) : undefined,
        path: url.pathname,
        query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        headers: headersRecord,
      };

      // Only include body if it exists (not for GET/DELETE)
      if (body !== undefined) {
        httpRequestOptions.body = body;
      }

      const httpRequest = new HttpRequest(httpRequestOptions);

      // Sign the request
      // uriEscapePath: false because the path is already URI-encoded
      const signer = new SignatureV4({
        credentials,
        region,
        service: 'execute-api',
        sha256: Sha256,
        uriEscapePath: false,
      });

      const signedRequest = await signer.sign(httpRequest, {
        signingDate: new Date(),
        signingRegion: region,
        signingService: 'execute-api',
        // Use UNSIGNED-PAYLOAD to avoid CORS issues with x-amz-content-sha256 header
        unsignableHeaders: new Set(['x-amz-content-sha256']),
      });

      // Apply only the required signed headers to axios config
      // We explicitly pick only the headers we need to avoid browser restrictions
      const authHeader = signedRequest.headers['authorization'] || signedRequest.headers['Authorization'];
      const dateHeader = signedRequest.headers['x-amz-date'] || signedRequest.headers['X-Amz-Date'];
      const tokenHeader =
        signedRequest.headers['x-amz-security-token'] || signedRequest.headers['X-Amz-Security-Token'];

      if (authHeader) config.headers.set('authorization', authHeader);
      if (dateHeader) config.headers.set('x-amz-date', dateHeader);
      if (tokenHeader) config.headers.set('x-amz-security-token', tokenHeader);

      return config;
    });
  }
}

export class EasyIdApi {
  private api: AxiosInstance;
  constructor() {
    this.api = getEasyIdApi();
  }

  set Api(api) {
    this.api = api;
  }

  get Api() {
    return this.api;
  }
}

const getEasyIdApi = () => {
  return axios.create({
    baseURL: process.env.REACT_APP_APP_USER_API_URL,
    headers: {
      'x-api-key': process.env.REACT_APP_APP_USER_API_KEY,
    },
  });
};

const easyIdInstance = new EasyIdApi();

Object.freeze(easyIdInstance);

export { easyIdInstance };
